<template>
    <section id="home">
        <section class="content">
            <h1>{{ $t(`home_page.${site}.title`) }}</h1>
            <p class="lead-in hide-desktop">{{ $t(`home_page.${site}.lead_in`) }}</p>

            <div class="search-box" ref="search">
                <router-link :to="{name: 'basic-search'}" class="search-toggle" exact>{{ $t('labels.basic_search') }}</router-link>
                <router-link :to="{name: 'advanced-search'}" class="search-toggle">{{ $t('labels.advanced_search') }}</router-link>
                <button id="help-toggle" @click="showHelpModal">
                    <span aria-hidden="true">?</span>
                    <span class="sr-only">{{ $t('labels.help') }}</span>
                </button>

                <SearchBox v-if="!advancedSearchUI" @loaded="scrollToResults"></SearchBox>
                <AdvancedSearchBox v-if="advancedSearchUI" @loaded="scrollToResults"></AdvancedSearchBox>
            </div>
        </section>

        <LandingContent v-if="!hasResults"></LandingContent>
        <section class="highlight" ref="highlight" v-if="getHash">
            <div class="image" :style="'background-image:url(' + getHighlightData.image + ')'">
                <div class="overlay">
                    <h2>{{ getHighlightData.content.title }}</h2>
                </div>
                
            </div>
            <div v-if="getHighlightData.content.detailed_description" class="content">
                <p v-for="item in getHighlightData.content.detailed_description" :key="item">{{ item }}</p>
            </div>
            <div v-else class="content">
                <p v-html="getHighlightData.content.description"></p>
            </div>
        </section>
        <section ref="results">
            <div v-if="loading" class="loading">
                <p class="sr-only">{{ $t('results_page.loading')}}</p>
                <div aria-hidden="true" class="spinner"></div>
            </div>
            <div>
                <Results v-if="hasResults" :advancedSearch="!advancedSearchUI" @loaded="scrollToResults"></Results>
            </div>
        </section>
    <JsonLD :data="json_ld"></JsonLD>
    </section>
</template>

<script>
import { mapGetters } from 'vuex'
import JsonLD from '../components/JsonLD'
import SearchBox from '../components/SearchBox'
import AdvancedSearchBox from '../components/AdvancedSearchBox'
import LandingContent from '../components/LandingContent'
import Results from '../components/Results'

export default {
    components: {
        SearchBox,
        AdvancedSearchBox,
        Results,
        LandingContent,
        JsonLD
    },
    head () {
        return {
            title: function() {
                return {
                    inner: this.$i18n.t(`main.${this.site}.site_name`),
                    separator: ' ',
                    complement: ' ',
                }
            },
            meta: function() {
                return [
                    {rel: 'canonical', href: this.$i18n.t(`main.${this.site}.cannonical`), id: 'rel-con'},
                    {rel: 'alternate', href: this.$i18n.t(`main.${this.site}.alternate`), hreflang: this.$i18n.t(`main.${this.site}.alternate_locale`), id: 'rel-alt'},

                    {name: 'description', content: this.$i18n.t(`main.${this.site}.description`), id: 'desc'},

                    {property: 'og:title', content: `${this.$i18n.t(`main.${this.site}.site_name`)} | ${this.$i18n.t(`main.${this.site}.museum_name`)}`, id: 'og-title'},
                    {property: 'og:description', content: this.$i18n.t(`main.${this.site}.cannonical`), id: 'og-desc'},
                    {property: 'og:type', content: 'website', id: 'og-type'},
                    {property: 'og:locale', content: this.$i18n.locale, id: 'og-locale'},
                    {property: 'og:image', content: `${window.location.host}${this.$i18n.t(`main.${this.site}.preview_image`)}`, id: 'og-image'}
                ]
            }
        }
    },
    data() {
        return {
            hash: null
        }
    },
    props: {
        'advancedSearchUI': Boolean,
        'params': Object
    },
    computed: {
        ...mapGetters({
            'saved_query' : 'search/query',
            'loading' : 'results/list_loading',
            'site': 'search/site',
            'results': 'results/list_results',
            'json_ld': 'search/json_ld'
        }),
        hasQuery () {
            return Object.keys(this.params).length > 0;
        },
        hasResults () {
            return Object.keys(this.results).length;
        },
        getHash(){
            return !this.hash ? window.location.hash : this.hash
        },
        getHighlightData(){
            let hash = this.getHash.replace('#', '')
            const content =  this.$i18n.t(`collections_page.${this.site}.collections`).filter(function(item){
                let title = item.title.toLowerCase().replaceAll(' ','-').normalize("NFD").replace(/\p{Diacritic}/gu, "").replace('’', '')
                return title === hash
            })

            let image = content[0].image.replace('images', 'img')
            return { hash : hash, content : content[0], image : image }
            //return this.$i18n.messages[this.$i18n.locale].home_page[this.site].featured_collections;
        }
    },
    watch: {
        '$route': function (val) { 
            this.hash = val.hash
        },
        'site': function () {
            this.$emit('updateHead')
        },
        loading(val){
            let el = this.$refs.highlight
            if (val === false && el ) {
                let isSorted = window.location.href.indexOf("&sort") > -1
                let results = this.$refs.results
                setTimeout(()=> {
                    !isSorted ? window.scrollTo(0, el.offsetTop) : window.scrollTo(0, results.offsetTop)
                })
            }
        }
    },
    methods: {
        showHelpModal () {
            this.$emit('openHelpModal');
        },
        scrollToResults (y) { 
            let search = this.$refs.search;
            let results = this.$refs.results;
            let el = search;
            if (y < 200) {
                el = results;
            }

            setTimeout(()=> {
                window.scrollTo(0, el.offsetTop);
            })
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (from.name == 'saved-list') {
                vm.$store.dispatch('list/clearSelected')
            }
        })
    }

}
</script>

<style scoped lang="scss">
.loading {
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    background: rgba(1,1,1,.6);

    .spinner {
        // https://loading.io/css/
        display: inline-block;
        width: 80px;
        height: 80px;

        &:after {
            content: " ";
            display: block;
            width: 64px;
            height: 64px;
            margin: 8px;
            border-radius: 50%;
            border: 6px solid $med-gray1;
            border-color: $med-gray1 transparent $med-gray1 transparent;
            animation: lds-dual-ring 1.2s linear infinite;
            }
            @keyframes lds-dual-ring {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }
    }
}
.content {
    text-align: center;
    margin-top: $s3;

    h1, p {
        max-width: calc(#{$max-text-width} + 40px);
        margin: $s2 auto;
    }

    .lead-in {
        margin-top: $s1 * .6;
        margin-bottom: $s1 * .6;
    }
     
    h1 {
        margin-bottom: 0;
    }

    .search-toggle {
        font-size: 1rem;
        text-decoration: none;
        color: $dark-gray2;
        margin-right: $s2;
        text-transform: uppercase;
        font-weight: bold;
        font-family: 'FuturaBold', Arial, sans-serif;
        margin-bottom: .5rem;
    }

    .router-link-active {
        color: $dark-gray1;
        text-decoration: underline;
        text-underline-offset: .5rem;
    }

    .search-box {
        text-align: left;
        max-width: 1230px;
        margin: 0 auto 6.094rem;
        padding: 4.375rem $s3 0;
    }

    #help-toggle {
        border-radius: 50%;
        color: $dark-gray1;
        border: solid 2px $dark-gray1;
        background: none;

        &:hover {
            cursor: pointer;
        }
    }
}


@media (max-width: $mq-max-width-tablet) {
    .content {
        padding-right: $side-padding;
        padding-left: $side-padding;
        .search-box {
            padding-top: 3.125rem;
            padding-right: 0;
            padding-left: 0;
            margin-bottom: 1.25rem;
        }
        .search-toggle {
            margin-right: 1.563rem;
        }
    }
}

@media (max-width: $mq-max-width-mobile) {
    .content {
        padding-right: $side-padding-mobile;
        padding-left: $side-padding-mobile;
  
        .search-toggle {
            margin-right: $s2;
            font-size: .85rem;
            margin-right: 1.563rem;
        }

        #help-toggle {
            font-size: .85rem;
            margin-top: 1rem;
        }

        .search-box {
            padding-top: 3.125rem;
            padding-right: 0;
            padding-left: 0;
            margin-bottom: 1.25rem;
        }
    }
}


@media print {
    .content,
    .top-options {
        display: none;
    }
}


.highlight{
    display: grid;
    grid-template-columns: 1fr 1fr;
    background-color:$dark-gray1;
    .image{
        background-position: center;
        background-size: cover;
        min-height: 50vh;
        @media (max-width: $mq-max-width-tablet) {
                min-height: 20vh;  
            }
        .overlay {
            height: 100%;
            width: 100%;
            background-color: rgba(0,0,0,0.5);
            h2 {
            padding: 4rem;
            color: white;
            margin: 0px;
            font-size: 5vw;
            @media (max-width: $mq-max-width-tablet) {
                font-size: 8vw;  
            }
            @media (min-width: $mq-max-width-desktop) {
                font-size: 63px;  
            }
        }
        }
        
    }
    .content{
        color: white;
        margin-top: 0rem;
        padding: 4rem;
        text-align: left;
        p {
            margin-left: 0px;
        }
        p:nth-child(odd) {
            margin: 0px;
        }

    }
    @media (max-width: $mq-max-width-tablet) {
        grid-template-columns: 1fr;
        
        
        .content {
            padding: 4rem;

        }
    }
}
@media (max-width: $mq-max-width-tablet ) {
    .highlight {
        grid-template-columns: 1fr;
    }
}
</style>